import { settings } from './storage.js';

/* Cliente del catálogo cinematográfico de Filoteca (pasarela pública de solo lectura: /api/catalog, perfil core = datos CC0).
   Se consulta por identificador exacto (IMDb o TMDB película/serie): nunca por parecido de título. Devuelve lo que sirve para
   completar una ficha (reparto con personajes, dirección, géneros, países, duración, año, identificadores) y nada más. */

const TIMEOUT_MS = 8000;

export function normCatalogWork(w) {
    if (!w || !w.id) return null;
    const names = (list) => (list || []).map((c) => (c && c.label && c.label.text) || '').filter(Boolean);
    const xid = (ns) => { const x = (w.external_ids || []).find((e) => e.namespace === ns && e.status === 'confirmed'); return x ? String(x.value) : ''; };
    const cast = ((w.credits_summary && w.credits_summary.cast) || []).map((c) => ({ name: (c.name && c.name.text) || '', character: (c.character && c.character.name) || '' })).filter((c) => c.name);
    return {
        workId: w.id, version: w.version, type: w.type,
        title: (w.title && w.title.text) || '', year: w.year || 0,
        directors: ((w.credits_summary && w.credits_summary.directors) || []).map((p) => (p.name && p.name.text) || '').filter(Boolean),
        genres: names(w.genres), countries: names(w.countries), languages: names(w.original_languages),
        minutes: w.duration && w.duration.value && w.duration.value.minutes ? Math.round(w.duration.value.minutes) : 0,
        cast, castTotal: (w.credits_summary && w.credits_summary.cast_total) || cast.length,
        imdbId: xid('imdb'), tmdbMovieId: xid('tmdb:movie'), tmdbTvId: xid('tmdb:tv'), website: (w.official_websites || [])[0] || ''
    };
}

export function createCatalogClient({ getBase, getLocale = () => 'es', fetchImpl = (...a) => fetch(...a) }) {
    const inflight = new Map();
    const asked = new Set();
    /** Pide al catálogo que incorpore obras que no tiene: solo identificadores, una vez por aparato y sin esperar la respuesta. */
    const ask = (ids) => {
        let list = [];
        try { list = JSON.parse(settings.get('catalogAsked') || '[]'); } catch (e) { list = []; }
        const fresh = ids.filter(([ns, v]) => !asked.has(ns + ':' + v) && list.indexOf(ns + ':' + v) === -1);
        if (!fresh.length) return;
        fresh.forEach(([ns, v]) => asked.add(ns + ':' + v));
        call('/requests', { items: fresh.map(([namespace, value]) => ({ namespace, value })) }).then((r) => {
            if (r) { list = list.concat(fresh.map(([ns, v]) => ns + ':' + v)).slice(-500); settings.set('catalogAsked', JSON.stringify(list)); }
        }).catch(() => { fresh.forEach(([ns, v]) => asked.delete(ns + ':' + v)); });
    };
    const call = async (path, body) => {
        const base = getBase();
        const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = ctl ? setTimeout(() => ctl.abort(), TIMEOUT_MS) : null;
        try {
            const r = await fetchImpl(base + '/api/catalog' + path, Object.assign({ headers: Object.assign({ Accept: 'application/json' }, body ? { 'Content-Type': 'application/json' } : {}), signal: ctl ? ctl.signal : undefined }, body ? { method: 'POST', body: JSON.stringify(body) } : {}));
            if (r.status === 404) return null;
            if (!r.ok) throw new Error('catálogo: HTTP ' + r.status);
            return await r.json();
        } finally { if (timer) clearTimeout(timer); }
    };
    const resolve = async (ns, value) => { const r = await call('/resolve?namespace=' + encodeURIComponent(ns) + '&value=' + encodeURIComponent(value)); return r && r.status === 'resolved' && r.entity_type === 'work' ? r.entity_id : null; };
    return {
        enabled() { return settings.get('catalogOn') !== false && !!getBase(); },
        /** Ficha del catálogo para un título con identificadores conocidos, o null si no está (o la correspondencia es ambigua). */
        lookup({ kind, imdb, tmdb, needPlot = false }) {
            const key = [kind, imdb || '', tmdb || '', needPlot ? 'p' : ''].join('|');
            if (inflight.has(key)) return inflight.get(key);
            const p = (async () => {
                const ids = [];
                if (/^tt\d+$/.test(String(imdb || ''))) ids.push(['imdb', String(imdb)]);
                if (/^\d+$/.test(String(tmdb || ''))) ids.push([kind === 'series' ? 'tmdb:tv' : 'tmdb:movie', String(tmdb)]);
                if (!ids.length) return null;
                const found = new Set();
                for (const [ns, v] of ids) { const id = await resolve(ns, v); if (id) found.add(id); }
                if (!found.size) { ask(ids); return null; } // no está: se pide (solo identificadores) para que el catálogo lo incorpore
                if (found.size !== 1) return null; // los identificadores discrepan: no se inventa
                const id = Array.from(found)[0];
                const w = await call('/works/' + encodeURIComponent(id) + '?locale=' + encodeURIComponent(getLocale()) + '&include=credits_summary,external_ids');
                const n = normCatalogWork(w);
                if (!n) return null;
                if (kind === 'series' && n.type !== 'series') return null;
                if (kind !== 'series' && n.type !== 'film') return null;
                if (needPlot) { try { const sy = await call('/works/' + encodeURIComponent(id) + '/synopsis?locale=' + encodeURIComponent(getLocale())); if (sy && sy.synopsis && sy.synopsis.text) n.synopsis = { text: sy.synopsis.text, language: sy.synopsis.language, license: sy.synopsis.license, attribution: sy.attribution && sy.attribution.attribution ? sy.attribution.attribution : '', licenseUrl: sy.attribution && sy.attribution.license_url ? sy.attribution.license_url : '', sourceUrl: sy.attribution && sy.attribution.source_url ? sy.attribution.source_url : '' }; } catch (e) { /* la sinopsis es opcional */ } }
                return n;
            })().finally(() => inflight.delete(key));
            inflight.set(key, p);
            return p;
        }
    };
}
